import { useCallback, useEffect, useState } from 'react';

import { ApiFailure, request } from '../../shared/api';
import {
  advantages,
  lockedFeatures,
  planName,
  priceMonthly,
  type Limits,
  type Offer,
} from '../../shared/plan';
import { ListSkeleton, renderFailure } from '../../shared/states';
import { useLoad } from '../../shared/use-load';
import { openLink } from '../../telegram/webapp';

/**
 * Экран оплаты (T12.6, `09_UI_UX.md` §8).
 *
 * Открывается в момент упора в лимит, а не заранее: показывать цену
 * тому, кто ещё не почувствовал ограничения, значит просить денег до
 * того, как продукт доказал, что нужен.
 *
 * ## Обещания собраны из ответа сервера
 *
 * «20 подписок вместо 1» и «уведомления сразу» строятся из
 * `GET /v1/plans`, а не выписаны в разметке. Лимиты живут в таблице
 * `plans` и меняются миграцией; обещание на экране оплаты,
 * разошедшееся с тем, что человек получит, — обман, за который
 * заплатили деньгами.
 *
 * ## Ключ идемпотентности — один на открытие экрана
 *
 * Он создаётся при первом нажатии и переиспользуется, пока экран
 * открыт: повторное нажатие «Оплатить» возвращает тот же счёт, а не
 * выставляет второй (`05_API.md` §11). Закрыл и открыл заново — новый
 * счёт, и это верно: прежний к тому времени человек оставил.
 *
 * ## Оплата уходит в браузер, а тариф приходит вебхуком
 *
 * Страница провайдера открывается вне приложения, и, вернувшись, человек
 * видит тот же экран. Проверка идёт сама, как только приложение снова
 * на виду: человек уже сделал всё, что от него требовалось, и просить
 * его нажать ещё одну кнопку — значит превращать успех в загадку.
 * Найдено живым платежом: экран продолжал предлагать «Оплатить», и
 * каждое нажатие выставляло новый счёт.
 *
 * Кнопка «Проверить оплату» рядом остаётся: возвращение не всегда
 * сопровождается событием — клиенты Telegram разные, — и человеку нужен
 * способ спросить самому.
 *
 * Между уходом на оплату и ответом провайдера проходят секунды: касса
 * подтверждает платёж не мгновенно. Поэтому проверок несколько, с
 * паузой, и они прекращаются, как только тариф найден.
 */

interface Checkout {
  readonly pay_url: string;
  readonly expires_at: string | null;
  readonly amount_minor: string | number;
}

interface Everything {
  readonly plans: Offer[];
  readonly limits: Limits;
}

/** Какой тариф продаём. Единственный платный из предлагаемых. */
const PAID_PLAN = 'pro';

function load(): Promise<Everything> {
  return Promise.all([request<Offer[]>('/v1/plans'), request<Limits>('/v1/users/me/limits')]).then(
    ([plans, limits]) => ({ plans, limits }),
  );
}

type Stage =
  | { readonly kind: 'idle' }
  | { readonly kind: 'creating' }
  | { readonly kind: 'opened' }
  | { readonly kind: 'checking' }
  | { readonly kind: 'paid' }
  | { readonly kind: 'failed'; readonly hint: string };

export function PaymentScreen({ onBack }: { onBack: () => void }): React.JSX.Element {
  const [state, retry] = useLoad<Everything>(useCallback(load, []));
  const [stage, setStage] = useState<Stage>({ kind: 'idle' });
  // Ключ живёт столько же, сколько открытый экран: повторное нажатие
  // обязано вернуть тот же счёт
  const [key] = useState(() => crypto.randomUUID());

  const pay = useCallback(() => {
    setStage({ kind: 'creating' });

    request<Checkout>('/v1/payments/checkout', {
      method: 'POST',
      body: { plan_code: PAID_PLAN },
      headers: { 'idempotency-key': key },
    })
      .then((checkout) => {
        setStage({ kind: 'opened' });
        // Через Telegram: внутри Mini App обычное окно блокируется, и
        // страница оплаты молча не открылась бы
        openLink(checkout.pay_url);
      })
      .catch((failure: unknown) => {
        setStage({
          kind: 'failed',
          hint:
            failure instanceof ApiFailure
              ? failure.message
              : 'Сервис не ответил. Это бывает; попробуйте ещё раз.',
        });
      });
  }, [key]);

  /**
   * Спрашивает сервер, оплачено ли.
   *
   * Правда о тарифе одна — та, что на сервере. Считать оплату
   * состоявшейся по возвращению человека значило бы обещать тариф,
   * которого нет.
   */
  const paidAlready = useCallback(async (): Promise<boolean> => {
    try {
      return (await request<Limits>('/v1/users/me/limits')).plan === PAID_PLAN;
    } catch {
      // Сеть подвела — это не «не оплачено», а «не знаем». Экран
      // остаётся прежним, и человек может спросить ещё раз
      return false;
    }
  }, []);

  /** Проверка по кнопке: видно, что она идёт. */
  const check = useCallback(() => {
    setStage({ kind: 'checking' });
    void paidAlready().then((paid) => {
      setStage(paid ? { kind: 'paid' } : { kind: 'opened' });
    });
  }, [paidAlready]);

  // Возвращение в приложение — сигнал, что человек со страницей оплаты
  // закончил. Спрашиваем сервер сами: событий об оплате Telegram не
  // присылает, а тариф к этому времени уже выдан вебхуком
  useEffect(() => {
    if (stage.kind !== 'opened') return undefined;

    // Тихо: показывать «Проверяю…» на каждое возвращение к экрану
    // значило бы мигать состоянием там, где человек ничего не просил.
    // Экран меняется, только когда есть что сообщить
    const again = (): void => {
      if (document.visibilityState !== 'visible') return;

      void paidAlready().then((paid) => {
        if (paid) setStage({ kind: 'paid' });
      });
    };

    document.addEventListener('visibilitychange', again);
    window.addEventListener('focus', again);

    // Первые секунды после возвращения касса может ещё не прислать
    // подтверждение: три попытки с паузой закрывают этот разрыв, а
    // дальше остаётся кнопка
    const timers = [2000, 6000, 15_000].map((delayMs) => setTimeout(again, delayMs));

    return () => {
      document.removeEventListener('visibilitychange', again);
      window.removeEventListener('focus', again);
      timers.forEach(clearTimeout);
    };
  }, [paidAlready, stage.kind]);

  if (state.kind === 'loading') return <ListSkeleton rows={2} />;

  if (state.kind === 'failed') {
    return <>{renderFailure(state.error, { onRetry: retry, onUpgrade: retry })}</>;
  }

  const { plans, limits } = state.value;
  const paid = plans.find((one) => one.code === PAID_PLAN);
  const free = plans.find((one) => one.code === 'free');

  if (stage.kind === 'paid' || limits.plan === PAID_PLAN) {
    return (
      <>
        <button type="button" className="back" onClick={onBack}>
          ← Назад
        </button>

        <div className="card subscribe">
          <p className="subscribe__title">{planName(limits.plan)} подключён</p>
          <p className="subscribe__hint">
            Уведомления приходят сразу, ограничения сняты. Спасибо — на это мы и живём.
          </p>
          <button type="button" className="button button--accent" onClick={onBack}>
            Вернуться
          </button>
        </div>
      </>
    );
  }

  // Тариф пропал из ответа сервера: снят с продажи миграцией. Такое
  // бывает, и показывать кнопку оплаты того, чего нет, нельзя
  if (paid === undefined) {
    return (
      <>
        <button type="button" className="back" onClick={onBack}>
          ← Назад
        </button>

        <div className="card subscribe">
          <p className="subscribe__title">Оплата сейчас недоступна</p>
          <p className="subscribe__hint">
            Тариф временно не продаётся. Напишите в поддержку — подключим вручную.
          </p>
        </div>
      </>
    );
  }

  const locked = lockedFeatures(limits.features);
  const rows = advantages(paid, free);
  const price = priceMonthly(paid.price_minor);

  return (
    <>
      <button type="button" className="back" onClick={onBack}>
        ← Назад
      </button>

      <div className="card offer">
        <div className="offer__head">
          <span className="offer__name">{paid.name}</span>
          <span className="offer__price">{price}</span>
        </div>

        <ul className="offer__list">
          {rows.map((row) => (
            <li key={row}>{row}</li>
          ))}
          {locked.map((feature) => (
            <li key={feature}>{feature.charAt(0).toUpperCase() + feature.slice(1)}</li>
          ))}
        </ul>

        {stage.kind === 'failed' ? <p className="subscribe__warning">{stage.hint}</p> : null}

        {stage.kind === 'opened' || stage.kind === 'checking' ? (
          <>
            <p className="subscribe__hint">
              Страница оплаты открыта в браузере. Оплатите и вернитесь сюда — тариф включится сам,
              обычно за несколько секунд.
            </p>
            <div className="subscribe__actions">
              <button
                type="button"
                className="button button--accent"
                disabled={stage.kind === 'checking'}
                onClick={check}
              >
                {stage.kind === 'checking' ? 'Проверяю…' : 'Проверить оплату'}
              </button>
              <button type="button" className="button" onClick={pay}>
                Открыть снова
              </button>
            </div>
          </>
        ) : (
          <button
            type="button"
            className="button button--accent"
            disabled={stage.kind === 'creating'}
            onClick={pay}
          >
            {stage.kind === 'creating'
              ? 'Готовлю счёт…'
              : `Оплатить ${price.replace(' в месяц', '')}`}
          </button>
        )}

        {/*
          Оплата уходит на страницу провайдера: карта и её данные через
          нас не проходят и у нас не хранятся (`08_SECURITY.md` §8), и
          сказать об этом стоит там, где человек решает платить
        */}
        <p className="offer__note">
          Оплата на стороне платёжного сервиса. Данные карты к нам не попадают.
        </p>
      </div>
    </>
  );
}
